import { Badge, Box, Flex, ActionIcon as MantineActionIcon, Text } from "@mantine/core";
import { IIssueApi, IIssueTodoItem } from "@schemas/issueSchema";
import { IconLock } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import { useElementSize } from "usehooks-ts";
import Avatar from "../../../../../../components/Avatar";
import { ActionIcon, IssueIcon } from "../../../../../../components/CustomIcons/CustomIcons";
import DisplayDueDate from "../../../../../../components/DisplayDueDate";
import TaskCompletedCount from "../../../../../../components/TaskCompletedCount/TaskCompletedCount";
import { ISSUE_COMPLETED_VALUE, ISSUE_EXPIRED, ISSUE_STATES_OPTIONS } from "../../../issueConstants";
import { selectIssueBoard } from "../../../issueSelectors";
import { getTaskCompleteCount, issueBoardHasClientAccess } from "../../../issueUtils";
import "./KanbanCard.css";

export const KanbanCard: React.FC<{
  content: IIssueApi;
  openContent: (content: IIssueApi) => void;
  isLappeplan?: boolean;
  isMobile?: boolean;
  onDisconnectNode?: (id: string) => void;
}> = ({ content, openContent, isLappeplan = false, onDisconnectNode, isMobile = false }) => {
  const [textElementRef, { height }] = useElementSize();
  const isComplete = content.status === ISSUE_COMPLETED_VALUE || content.status === ISSUE_EXPIRED;
  const issueBoard = useSelector(selectIssueBoard);
  const showClientLock = issueBoardHasClientAccess(issueBoard) && content.clientAccess !== true;

  const matchStatusColor = (status: string) => {
    const color = ISSUE_STATES_OPTIONS.find((s) => s.value === status)?.color;
    return color || "";
  };

  let todos = content.todos as IIssueTodoItem[];

  if (issueBoard?.showCompletedTodosInKanban === false) {
    todos = todos?.filter((todo) => todo.completedDate == null) ?? [];
  }

  return (
    <Box
      w={isMobile ? "100%" : 250}
      mb={8}
      onClick={() => openContent(content)}
      style={{ borderRadius: 6, overflow: "hidden", cursor: "pointer" }}
      className="shadowThin"
    >
      {/* Header */}
      <Flex bg="dark" px="xs" gap="xxs" align="center" style={{ height: 30, borderRadius: "6px 6px 0 0" }}>
        {content.taskOrIssue === "ISSUE" && (
          <IssueIcon style={{ fontSize: "0.8em", color: "white", marginRight: "2px" }} />
        )}
        {content.taskOrIssue === "TASK" && <ActionIcon style={{ fontSize: "1em", color: "white" }} />}
        <Text c="warmgray.1" fw={700} size="sm">
          {content.id}
        </Text>
        {showClientLock && <IconLock color="var(--mantine-color-red-5)" size={16} style={{ flexShrink: 0 }} />}
        <Box style={{ flex: 1 }} />
        {content.connectedTasks?.length > 0 && (
          <Box mr={8}>
            <TaskCompletedCount taskCompleteCount={getTaskCompleteCount(content, true)} useActionIcon />
          </Box>
        )}
        {content?.priority === "HIGH" && <Box mr={8} className="statusdot" style={{ backgroundColor: "#dc3545" }} />}
        {isLappeplan && (
          <MantineActionIcon
            size="sm"
            variant="subtle"
            color="warmgray.1"
            onClick={(e) => {
              e.stopPropagation();
              onDisconnectNode?.(content._id);
            }}
            aria-label="Disconnect node"
          >
            <i className="fa fa-times" />
          </MantineActionIcon>
        )}
      </Flex>

      {/* Body */}
      <Flex
        direction="column"
        bg="white"
        p="xxs"
        gap="xxs"
        style={{
          fontSize: 12,
          maxHeight: content.showTodosInKanban === true ? "none" : 250,
          overflow: "hidden",
        }}
      >
        <Box
          className="contentTitle"
          style={{
            boxShadow: `inset 8px 0 0px -5px ${matchStatusColor(content.status)}a6`,
          }}
        >
          <Text size="sm" lh="xs" fw={500}>
            {content.title}
          </Text>
        </Box>

        {/* Description or Todos */}
        {content.showTodosInKanban !== true ? (
          <div
            ref={textElementRef}
            className="body-text-truncate px-1"
            style={{ WebkitLineClamp: Math.floor(height / 16) || 3 }}
          >
            {content.description}
          </div>
        ) : (
          <Box>
            {todos.map((todo) => (
              <Text key={todo._id as string} size="xs" m={0} p={0}>
                {todo.completedDate != null ? (
                  <i className="fa fa-check-circle fa-fw success-text" />
                ) : (
                  <i className="fa fa-circle fa-fw gray-text" />
                )}{" "}
                {todo.text}
              </Text>
            ))}
          </Box>
        )}

        {/* Tags */}
        {content.tags?.length > 0 && (
          <Flex wrap="wrap" align="flex-end" gap={2}>
            {content.tags.map((tag, index) => (
              <Badge key={`${tag}-${index}`} className="greybadge" size="sm" radius="4px" fz="11px">
                {tag}
              </Badge>
            ))}
          </Flex>
        )}

        {/* Footer: Assignee + Due Date */}
        <Flex align="center" m="xxs" gap={0}>
          <Avatar id={content?.assignedTo?._id} name={content?.assignedTo?.name} size={20} />
          <Text size="xs" c="warmgray.8" className="truncateHeaderText" ml={4}>
            {content?.assignedTo?.name}
          </Text>
          <Box style={{ flex: 1 }} />
          <Box style={{ fontSize: "1.1em" }}>
            <DisplayDueDate dueDate={content.dates?.due} isComplete={isComplete} />
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
};
